import type {
  MediapipeHandResults,
  MediapipeLandmark,
  MediapipePoseResults,
} from "/@/shared/motion-menu-system/MenuTypes";
// @ts-expect-error Vite-specific import — no ambient types in this config.
import VisionWorkerCtor from "./VisionWorker.ts?worker&inline";

// Bail out of a stalled detect() rather than blocking the animation loop.
const DETECT_TIMEOUT_MS = 5000;

// Segmentation doesn't need to match the render frame rate.
const SEG_MIN_INTERVAL_MS = 1000 / 15; // 15 fps

// ─── Pose stabilization constants ────────────────────────────────────────────
const POSE_SMOOTHING_FACTOR = 0.7;
const MAX_POSE_MOVEMENT_THRESHOLD = 0.05;
const LARGE_MOVEMENT_ADAPTATION_RATE = 0.2;
const POSE_MEMORY_DURATION = 1000;

function distance3(a: any, b: any): number {
  return Math.sqrt(
    (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2,
  );
}

function smoothLandmarks(
  current: any[],
  previous: any[],
  smoothing: number,
  threshold: number,
  adaptRate: number,
): any[] {
  return current.map((lm, i) => {
    const prev = previous[i];
    if (!prev) return lm;
    const dist = distance3(lm, prev);
    const s = dist < threshold ? smoothing : smoothing * (1 - adaptRate);
    return {
      x: prev.x + (lm.x - prev.x) * (1 - s),
      y: prev.y + (lm.y - prev.y) * (1 - s),
      z: prev.z + (lm.z - prev.z) * (1 - s),
    };
  });
}
// ─────────────────────────────────────────────────────────────────────────────

export interface VisionResult {
  hand?: MediapipeHandResults;
  pose?: MediapipePoseResults;
  /** Present only on frames where segmentation actually ran (throttled to 15fps). */
  segmentation?: {
    maskData: Uint8Array;
    maskWidth: number;
    maskHeight: number;
  };
}

type Pending = {
  resolve: (value: VisionResult) => void;
  reject: (reason: unknown) => void;
};

export class VisionDetector {
  private worker: Worker | undefined;
  private pending: Map<number, Pending> = new Map();
  private nextId = 1;
  private _isReady = false;
  private initPromise: Promise<void> | undefined;
  private detecting = false;
  private crashed = false;
  private lastSegTime = 0;

  // ─── Pose stabilization state ───────────────────────────────────────────────
  private smoothedPose: any = null;
  private smoothedPoseWorld: any = null;
  private lastGoodPoseTime = 0;
  private lastValidPose: any = null;
  private lastValidPoseWorld: any = null;
  // ────────────────────────────────────────────────────────────────────────────

  private handleWorkerCrash(err: ErrorEvent): void {
    console.error("[VisionDetector] Worker crashed:", err?.message ?? err);
    this.crashed = true;
    this._isReady = false;
    this.detecting = false;
    for (const { reject } of this.pending.values()) {
      reject(new Error("VisionWorker crashed"));
    }
    this.pending.clear();
  }

  init(): Promise<void> {
    if (this._isReady) return Promise.resolve();
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise<void>((resolve, reject) => {
      const worker: Worker = new VisionWorkerCtor();
      this.worker = worker;

      worker.onmessage = (event: MessageEvent) => {
        const msg = event.data;
        if (!msg || typeof msg !== "object") return;

        if (msg.cmd === "ready") {
          this._isReady = true;
          console.log(`[OFFSET-DIAG] VisionDetector worker ready`);
          worker.onerror = (err) => this.handleWorkerCrash(err);
          resolve();
          return;
        }

        if (msg.cmd === "error") {
          reject(new Error(msg.error ?? "VisionWorker init failed"));
          return;
        }

        if (msg.cmd === "result") {
          const entry = this.pending.get(msg.id);
          if (!entry) return;
          this.pending.delete(msg.id);
          if (msg.error) {
            entry.reject(new Error(msg.error));
          } else {
            entry.resolve({
              hand: msg.hand,
              pose: msg.pose,
              segmentation: msg.segmentation,
            });
          }
        }
      };

      worker.onerror = (err) => {
        reject(err);
        this.handleWorkerCrash(err);
      };

      worker.postMessage({ cmd: "init" });
    });

    return this.initPromise;
  }

  async detect(
    video: HTMLVideoElement,
    timestampMs: number,
    opts: { runHand: boolean; runPose: boolean; runSeg: boolean },
  ): Promise<VisionResult | undefined> {
    if (!this._isReady || !this.worker || this.detecting || this.crashed) {
      return undefined;
    }
    this.detecting = true;

    const bitmap = await createImageBitmap(video);
    if (!this.worker || this.crashed) {
      bitmap.close();
      this.detecting = false;
      return undefined;
    }

    // Throttle segmentation independently of hand/pose.
    const now = performance.now();
    const runSeg = opts.runSeg &&
      (now - this.lastSegTime >= SEG_MIN_INTERVAL_MS);
    if (runSeg) this.lastSegTime = now;

    const id = this.nextId++;
    const worker = this.worker;

    return new Promise<VisionResult | undefined>((callerResolve) => {
      let callerDone = false;

      // Called when the worker actually responds (or crashes).
      // This is the ONLY place that resets this.detecting, so no new
      // detect() calls can start until the worker confirms it is done.
      const complete = (result: VisionResult | undefined) => {
        this.detecting = false;
        if (!callerDone) {
          callerDone = true;
          callerResolve(result);
        }
      };

      this.pending.set(id, {
        resolve: (v) => complete(v),
        reject: () => complete(undefined),
      });

      worker.postMessage(
        {
          cmd: "detect",
          id,
          bitmap,
          timestampMs,
          runHand: opts.runHand,
          runPose: opts.runPose,
          runSeg,
        },
        [bitmap],
      );

      // If the worker is slow, unblock the caller so the animation loop keeps
      // running — but keep detecting=true so no new requests pile up.
      // When the worker eventually responds, complete() will reset detecting.
      setTimeout(() => {
        if (!callerDone) {
          callerDone = true;
          callerResolve(undefined);
          // detecting intentionally stays true here
        }
      }, DETECT_TIMEOUT_MS);
    });
  }

  // ─── Post-processing (previously on HandDetector / PoseDetector) ───────────

  applyHandFlip(results: MediapipeHandResults): MediapipeHandResults {
    const flip = (arr: MediapipeLandmark[][] | undefined) =>
      arr?.map((set) =>
        set.map((lm) => {
          const l = { ...lm };
          l.x = 1 - l.x;
          delete l.visibility;
          return l;
        })
      );
    return {
      ...results,
      landmarks: flip(results.landmarks),
      worldLandmarks: flip(results.worldLandmarks),
    };
  }

  applyPoseFlip(results: MediapipePoseResults): MediapipePoseResults {
    const flip = (arr: MediapipeLandmark[][] | undefined) =>
      arr?.map((set) =>
        set.map((lm) => {
          const l = { ...lm };
          l.x = 1 - l.x;
          delete l.visibility;
          return l;
        })
      );
    return { ...results, landmarks: flip(results.landmarks) };
  }

  stabilizePose(
    results: MediapipePoseResults,
    timestampMs: number,
  ): MediapipePoseResults {
    if (!results?.landmarks || !results?.worldLandmarks) return results;

    const currentPose = results.landmarks[0];
    const currentWorld = results.worldLandmarks[0];
    const isPoseReliable = currentPose && currentPose.length > 0;

    if (isPoseReliable) {
      this.lastValidPose = currentPose;
      this.lastValidPoseWorld = currentWorld;
      this.lastGoodPoseTime = timestampMs;

      this.smoothedPose = this.smoothedPose
        ? smoothLandmarks(
          currentPose,
          this.smoothedPose,
          POSE_SMOOTHING_FACTOR,
          MAX_POSE_MOVEMENT_THRESHOLD,
          LARGE_MOVEMENT_ADAPTATION_RATE,
        )
        : [...currentPose];

      if (currentWorld?.length > 0) {
        this.smoothedPoseWorld = this.smoothedPoseWorld
          ? smoothLandmarks(
            currentWorld,
            this.smoothedPoseWorld,
            POSE_SMOOTHING_FACTOR,
            MAX_POSE_MOVEMENT_THRESHOLD,
            LARGE_MOVEMENT_ADAPTATION_RATE,
          )
          : [...currentWorld];
      }
    } else {
      const age = timestampMs - this.lastGoodPoseTime;
      if (age >= POSE_MEMORY_DURATION || !this.lastValidPose) {
        return {
          ...results,
          landmarks: null as any,
          worldLandmarks: null as any,
        };
      }
    }

    return {
      ...results,
      landmarks: this.smoothedPose ? [this.smoothedPose] : results.landmarks,
      worldLandmarks: this.smoothedPoseWorld
        ? [this.smoothedPoseWorld]
        : results.worldLandmarks,
    };
  }

  // ──────────────────────────────────────────────────────────────────────────

  get isReady(): boolean {
    return this._isReady;
  }

  dispose(): void {
    console.log(`[OFFSET-DIAG] VisionDetector.dispose()`);
    for (const { reject } of this.pending.values()) {
      reject(new Error("VisionDetector disposed"));
    }
    this.pending.clear();
    if (this.worker) {
      try {
        this.worker.postMessage({ cmd: "close" });
      } catch {
        // Worker may already be terminated.
      }
      this.worker.terminate();
      this.worker = undefined;
    }
    this._isReady = false;
    this.initPromise = undefined;
    this.detecting = false;
    this.crashed = false;
    this.lastSegTime = 0;
  }
}
